import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebarNavigation from '../../components/ui/AdminSidebarNavigation';
import FilterPanel from '../admin-dashboard/components/FilterPanel';
import RegistrationTable from '../admin-dashboard/components/RegistrationTable';
import Button from '../../components/ui/Button';
import { adminService } from '../../services/adminService';
import { registrationService } from '../../services/registrationService';
import { exportService } from '../../services/exportService';
import { useAuth } from '../../contexts/AuthContext';

const AdminRegistrations = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [userRole, setUserRole] = useState('super_admin');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // Data states
  const [registrationsData, setRegistrationsData] = useState([]);

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1
  });

  const [filters, setFilters] = useState({
    city: 'all',
    ageGroup: 'all',
    gender: 'all',
    status: 'all',
    schoolNames: [], // Changed to array for multi-select
    dateFrom: ''
  });

  useEffect(() => {
    const storedRole = localStorage.getItem('userRole');
    if (storedRole) {
      setUserRole(storedRole);
      // Redirect comms_user to Communications page
      if (storedRole === 'comms_user') {
        navigate('/admin-communication-center');
        return;
      }
    }
    loadRegistrations();
  }, [navigate]);

  useEffect(() => {
    // Reset to page 1 when filters change
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [filters]);

  useEffect(() => {
    loadRegistrations();
  }, [filters, pagination.page]);

  const loadRegistrations = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await adminService.getFilteredRegistrations(filters, {
        page: pagination.page,
        pageSize: pagination.pageSize
      });
      setRegistrationsData(result?.data || []);
      setPagination(prev => ({
        ...prev,
        total: result?.total || 0,
        totalPages: result?.totalPages || 1
      }));
    } catch (err) {
      console.error('Error loading registrations:', err);
      setError(err?.message || 'Failed to load registrations');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      city: 'all',
      ageGroup: 'all',
      gender: 'all',
      status: 'all',
      schoolNames: [], // Changed to array for multi-select
      dateFrom: ''
    });
  };

  const handleExport = async (exportType = 'registration') => {
    try {
      const format = 'Excel'; // Default to Excel, can be made configurable
      
      // Get actual record count before exporting
      const recordCount = await exportService.getRecordCount('registration', filters);
      
      const exportResult = await exportService.exportRegistrations(format, filters);
      
      // Save export history with actual record count and file information
      const historyResult = await exportService.saveExportHistory(
        exportType, 
        format, 
        filters, 
        recordCount,
        exportResult?.filePath || null,
        exportResult?.fileName || null,
        exportResult?.fileSize || null
      );
      if (!historyResult.success) {
        console.warn('Failed to save export history:', historyResult.error);
        // Optionally show a warning to user
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      alert(`Failed to export data: ${error.message}`);
    }
  };

  const handleViewDetails = (registrationId) => {
    // Get the actual database ID
    const registration = registrationsData.find(r => (r.id === registrationId || r.dbId === registrationId || String(r.id) === String(registrationId) || String(r.dbId) === String(registrationId)));
    const dbId = registration?.dbId || registration?.id || registrationId;
    navigate('/registration-detail-view', { state: { registrationId: dbId, userMode: false } });
  };

  const handleBulkAction = async (action, selectedIds) => {
    if (!selectedIds || selectedIds.length === 0) {
      alert('Please select at least one registration');
      return;
    }

    if (!user?.id) {
      alert('User authentication required');
      return;
    }

    // Check for draft registrations
    const draftRegistrations = selectedIds.filter(id => {
      const registration = registrationsData.find(r => 
        (r.id === id || r.dbId === id || String(r.id) === String(id) || String(r.dbId) === String(id))
      );
      return registration && registration.status === 'draft';
    });

    if (draftRegistrations.length > 0) {
      alert(`Cannot ${action} ${draftRegistrations.length} registration(s) that are in draft state. Draft registrations must be submitted first.`);
      return;
    }

    // Check for already rejected registrations (only for reject action)
    if (action === 'reject') {
      const rejectedRegistrations = selectedIds.filter(id => {
        const registration = registrationsData.find(r => 
          (r.id === id || r.dbId === id || String(r.id) === String(id) || String(r.dbId) === String(id))
        );
        return registration && registration.status === 'rejected';
      });

      if (rejectedRegistrations.length > 0) {
        alert(`${rejectedRegistrations.length} registration(s) are already rejected. You cannot reject them again.`);
        return;
      }

      // Check for approved registrations - ask for confirmation
      const approvedRegistrations = selectedIds.filter(id => {
        const registration = registrationsData.find(r => 
          (r.id === id || r.dbId === id || String(r.id) === String(id) || String(r.dbId) === String(id))
        );
        return registration && registration.status === 'approved';
      });

      if (approvedRegistrations.length > 0) {
        const confirm = window.confirm(
          `Warning: ${approvedRegistrations.length} of the selected registration(s) are already approved. Rejecting them will change their status to rejected and this action cannot be easily undone.\n\nAre you absolutely sure you want to proceed?`
        );
        if (!confirm) {
          return;
        }
      }
    }

    // Check for rejected registrations when approving - ask for confirmation
    if (action === 'approve') {
      const rejectedRegistrations = selectedIds.filter(id => {
        const registration = registrationsData.find(r => 
          (r.id === id || r.dbId === id || String(r.id) === String(id) || String(r.dbId) === String(id))
        );
        return registration && registration.status === 'rejected';
      });

      if (rejectedRegistrations.length > 0) {
        const confirm = window.confirm(
          `Warning: ${rejectedRegistrations.length} of the selected registration(s) have been rejected. Approving them will change their status to approved and this action cannot be easily undone.\n\nAre you absolutely sure you want to proceed?`
        );
        if (!confirm) {
          return;
        }
      }
    }

    const actionText = action === 'approve' ? 'approve' : action === 'reject' ? 'reject' : 'process';
    const confirmMessage = `Are you sure you want to ${actionText} ${selectedIds.length} registration(s)?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setBulkActionLoading(true);
    try {
      const results = [];
      const errors = [];

      for (const id of selectedIds) {
        try {
          // Get the actual database ID from the registration
          const registration = registrationsData.find(r => (r.id === id || r.dbId === id || String(r.id) === String(id) || String(r.dbId) === String(id)));
          const dbId = registration?.dbId || registration?.id || id;

          if (action === 'approve') {
            await registrationService.approveRegistration(dbId, user.id);
            results.push({ id: dbId, success: true });
          } else if (action === 'reject') {
            // For bulk reject, use a generic reason
            await registrationService.rejectRegistration(dbId, user.id, 'Bulk rejection - please contact admin for details');
            results.push({ id: dbId, success: true });
          }
        } catch (err) {
          console.error(`Error ${action}ing registration ${id}:`, err);
          errors.push({ id, error: err.message });
        }
      }

      if (errors.length > 0) {
        alert(`${results.length} registration(s) ${actionText}d successfully. ${errors.length} failed.`);
      } else {
        alert(`Successfully ${actionText}d ${results.length} registration(s)`);
      }

      // Reload registrations
      await loadRegistrations();
    } catch (err) {
      console.error(`Error performing bulk ${action}:`, err);
      alert(`Failed to ${action} registrations: ${err.message}`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userRole');
    navigate('/mobile-authentication');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading registrations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="bg-destructive/10 text-destructive p-4 rounded-lg mb-4">
            <p className="font-semibold mb-2">Error Loading Registrations</p>
            <p className="text-sm">{error}</p>
          </div>
          <Button onClick={loadRegistrations}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminSidebarNavigation 
        isCollapsed={isSidebarCollapsed}
        onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <div className={`main-content with-sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Registrations</h1>
            <p className="text-muted-foreground">
              Manage and review team registrations for MI Junior Cricket Tournament.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" iconName="Bell" size="icon">
              <span className="sr-only">Notifications</span>
            </Button>
            <Button variant="outline" onClick={handleLogout} iconName="LogOut">
              Logout
            </Button>
          </div>
        </div>

        <div className="mb-6">
          <FilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleResetFilters}
            onExport={handleExport}
          />
        </div>

        <div>
          <RegistrationTable
            registrations={registrationsData}
            onViewDetails={handleViewDetails}
            onBulkAction={handleBulkAction}
            userRole={userRole}
            pagination={pagination}
            onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminRegistrations;

